import { readCardListFilters, describeFilters, readCardData } from "../card_list_filter.js";
import { flushPendingSaves } from "../save_card.js";
import { setCardQuantities } from "../display_user_card_quantities.js";
import { showToastMessage } from "../toast_message.js";

const BULK_TOAST_MS = 8000;

export function matchesBulkAction(card, action){
    if (action === "own"){
        return card.owned === 0;
    }
    if (action === "want"){
        return card.owned === 0 && card.wanted === 0;
    }
    return card.owned > 0;
}

function cardsWord(count){
    return count === 1 ? "card" : "cards";
}

export function describeBulkAction(action, { groupName, count, ownedCount = 0, filtered = false, quantityMode = false }){
    const shown = filtered ? " shown" : "";
    const cards = cardsWord(count);

    if (action === "own"){
        const kept = quantityMode ? " Cards you already own keep their counts." :
            (!filtered && ownedCount > 0 ? ` The ${ownedCount} you already own stay as they are.` : "");
        return {
            title: filtered ? `Mark ${count} shown ${groupName} ${cards} owned?` : `Mark all of ${groupName} owned?`,
            body: `This marks the ${count}${shown} ${groupName} ${cards} you don't have yet as owned.${kept}`,
            confirmLabel: `Mark ${count} owned`,
            confirmClass: "btn-primary"
        };
    }
    if (action === "want"){
        return {
            title: `Want ${count}${shown} missing ${groupName} ${cards}?`,
            body: `This adds the ${count}${shown} ${groupName} ${cards} you don't own to your want list.`,
            confirmLabel: `Want ${count} ${cards}`,
            confirmClass: "btn-primary"
        };
    }
    return {
        title: filtered ? `Clear owned on ${count} shown ${cards}?` : `Clear owned on all of ${groupName}?`,
        body: `This unmarks ${count}${shown} owned ${groupName} ${cards}. Trade and want marks are kept.`,
        confirmLabel: `Clear ${count} ${cards}`,
        confirmClass: "btn-danger"
    };
}

function doneMessage(action, count){
    const cards = `${count} ${cardsWord(count)}`;
    if (action === "own"){
        return `Marked ${cards} owned`;
    }
    if (action === "want"){
        return `Added ${cards} to your wants`;
    }
    return `Cleared owned on ${cards}`;
}

export async function postBulkUpdate(body){
    const response = await fetch('/user/cards/bulk', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success){
        const error = new Error(data.error || "Couldn't update these cards. Please try again.");
        error.updated = data.updated || [];
        error.previous = data.previous || [];
        throw error;
    }
    return data;
}

function visibleRows(group){
    const all = [...group.querySelectorAll('.card-rows > .card-row')];
    return { all, visible: all.filter(row => !row.hidden) };
}

export function initBulkActions(root, { saveTimers, onApplied }){
    const modalEl = root.querySelector('#bulk-confirm-modal');
    if (!modalEl || typeof bootstrap === 'undefined'){
        return;
    }
    // Bootstrap modals need to sit outside the list's sticky, stacked containers
    document.body.appendChild(modalEl);
    const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
    const confirmButton = modalEl.querySelector('[data-bulk-confirm]');
    const spinner = modalEl.querySelector('[data-bulk-spinner]');
    let pending = null;
    let busy = false;

    const applyUpdates = (updated) => {
        updated.forEach(setCardQuantities);
        onApplied();
    };

    const undo = async (previous) => {
        try {
            const result = await postBulkUpdate({ action: "restore", cards: previous });
            applyUpdates(result.updated);
            showToastMessage("Bulk change undone", "info");
        } catch (error) {
            applyUpdates(error.updated);
            showToastMessage("Couldn't undo that change. Please check the cards and try again.", "danger");
        }
    };

    const refreshMenu = (group) => {
        const { all, visible } = visibleRows(group);
        const filtered = visible.length < all.length;
        const shown = filtered ? " shown" : "";
        const cards = visible.map(readCardData);
        const note = group.querySelector('[data-bulk-filter-note]');
        if (note){
            note.hidden = !filtered;
            note.querySelector('[data-bulk-filter-text]').textContent =
                `Filters are on. These only change the ${visible.length} ${cardsWord(visible.length)} shown.`;
        }
        const captions = {
            own: (n) => `${n}${shown} not owned yet`,
            want: (n) => `${n}${shown} missing and not wanted`,
            clear: (n) => `${n}${shown} owned`
        };
        group.querySelectorAll('[data-bulk-action]').forEach(button => {
            const action = button.dataset.bulkAction;
            const count = cards.filter(card => matchesBulkAction(card, action)).length;
            button.disabled = count === 0;
            button.querySelector('[data-bulk-caption]').textContent = captions[action](count);
        });
    };

    const open = (group, action) => {
        const { all, visible } = visibleRows(group);
        const targets = visible.filter(row => matchesBulkAction(readCardData(row), action));
        if (!targets.length){
            return;
        }
        const filtered = visible.length < all.length;
        const copy = describeBulkAction(action, {
            groupName: group.querySelector('.card-group-title').textContent.trim(),
            count: targets.length,
            ownedCount: all.filter(row => Number(row.dataset.bsOwned) > 0).length,
            filtered,
            quantityMode: root.classList.contains('card-list-qty')
        });
        modalEl.querySelector('[data-bulk-title]').textContent = copy.title;
        modalEl.querySelector('[data-bulk-body]').textContent = copy.body;
        const filtersNote = modalEl.querySelector('[data-bulk-filters]');
        filtersNote.hidden = !filtered;
        modalEl.querySelector('[data-bulk-filters-text]').textContent = filtered ?
            `Filtered by ${describeFilters(readCardListFilters(root))}. Cards your filters hide aren't changed.` : "";
        confirmButton.className = `btn ${copy.confirmClass}`;
        modalEl.querySelector('[data-bulk-confirm-label]').textContent = copy.confirmLabel;
        pending = { action, cardIds: targets.map(row => row.id) };
        modal.show();
    };

    const setBusy = (value) => {
        busy = value;
        confirmButton.disabled = value;
        spinner.hidden = !value;
    };

    root.addEventListener('show.bs.dropdown', (event) => {
        const group = event.target.closest('.card-group-item');
        if (group && group.querySelector('[data-bulk-action]')){
            refreshMenu(group);
        }
    });

    root.addEventListener('click', (event) => {
        const button = event.target.closest('[data-bulk-action]');
        if (button && !button.disabled){
            open(button.closest('.card-group-item'), button.dataset.bulkAction);
        }
    });

    modalEl.addEventListener('hide.bs.modal', (event) => {
        if (busy){
            event.preventDefault();
        }
    });

    confirmButton.addEventListener('click', async () => {
        if (!pending || busy){
            return;
        }
        const { action, cardIds } = pending;
        setBusy(true);
        try {
            await flushPendingSaves(saveTimers);
            const result = await postBulkUpdate({ action, card_ids: cardIds });
            applyUpdates(result.updated);
            if (result.updated.length){
                showToastMessage(doneMessage(action, result.updated.length), "success", BULK_TOAST_MS,
                    { label: "Undo", onClick: () => undo(result.previous) });
            } else {
                showToastMessage("Nothing to change. These cards were already up to date.", "info");
            }
        } catch (error) {
            applyUpdates(error.updated);
            showToastMessage(error.message, "danger", BULK_TOAST_MS,
                error.previous.length ? { label: "Undo saved changes", onClick: () => undo(error.previous) } : null);
        } finally {
            setBusy(false);
            pending = null;
            modal.hide();
        }
    });
}
